
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Edit, MessageSquare } from "lucide-react";

interface JobGenerationPreviewProps {
  generatedJob: any;
  onRefine: (feedback: string) => void;
  isRefining: boolean;
}

export function JobGenerationPreview({ 
  generatedJob, 
  onRefine, 
  isRefining 
}: JobGenerationPreviewProps) {
  const [showRefinement, setShowRefinement] = useState(false);
  const [refinementFeedback, setRefinementFeedback] = useState("");

  const handleRefine = () => {
    if (refinementFeedback.trim()) {
      onRefine(refinementFeedback);
      setRefinementFeedback("");
      setShowRefinement(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Generated Job Package</h3>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowRefinement(!showRefinement)}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Provide Feedback
        </Button>
      </div>

      {showRefinement && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Refinement Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Textarea
                placeholder="Tell the AI what you'd like to improve or change..."
                value={refinementFeedback}
                onChange={(e) => setRefinementFeedback(e.target.value)}
                rows={3}
              />
              <div className="flex gap-2">
                <Button onClick={handleRefine} disabled={isRefining || !refinementFeedback.trim()}>
                  {isRefining ? "Refining..." : "Apply Feedback"}
                </Button>
                <Button variant="outline" onClick={() => setShowRefinement(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="description" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="description">Description</TabsTrigger>
          <TabsTrigger value="requirements">Requirements</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>

        <TabsContent value="description">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Job Description
                <Edit className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap text-sm">
                {generatedJob.description}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requirements">
          <Card>
            <CardHeader>
              <CardTitle>Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap text-sm">
                {generatedJob.requirements}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="skills">
          <Card>
            <CardHeader>
              <CardTitle>Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-sm mb-2">Must-Have Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {generatedJob.skills?.mustHave?.map((skill: string, index: number) => (
                      <Badge key={index} variant="default">{skill}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm mb-2">Nice-to-Have Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {generatedJob.skills?.niceToHave?.map((skill: string, index: number) => (
                      <Badge key={index} variant="secondary">{skill}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Job Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Salary Range:</span>
                  <p>{generatedJob.salaryRange || "Competitive"}</p>
                </div>
                <div>
                  <span className="font-medium">Experience Required:</span>
                  <p>{generatedJob.experienceRequired || "As specified"}</p>
                </div>
                <div>
                  <span className="font-medium">Benefits:</span>
                  <p>{generatedJob.benefits || "Standard package"}</p>
                </div>
                <div>
                  <span className="font-medium">Application Instructions:</span>
                  <p>{generatedJob.applicationInstructions || "Apply through our portal"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
